/**
 * Hashing delle password — PBKDF2-SHA256 via WebCrypto (§ 7 della specifica tecnica).
 *
 * Nessuna dipendenza Node-only: il modulo gira identico nel runtime Workers e nel browser.
 * Le password in chiaro non escono mai da qui: non vanno in log, in audit, nelle risposte
 * API né nei messaggi di errore.
 */

/**
 * Iterazioni di PBKDF2.
 *
 * La specifica ne chiede **almeno** 100 000; il runtime di Cloudflare ne ammette **al
 * massimo** 100 000 — oltre, `crypto.subtle.deriveBits` solleva
 * `Pbkdf2 failed: iteration counts above 100000 are not supported`. Il pavimento e il
 * soffitto coincidono, e questo è l'unico valore che soddisfa entrambi.
 *
 * Attenzione: il limite **non è applicato dal runtime locale di sviluppo**. Un valore più
 * alto passa i test, passa `wrangler pages dev`, e fallisce solo in produzione — dove
 * l'unico sintomo è che nessuno riesce più ad accedere. Il test qui accanto verifica
 * entrambi gli estremi proprio perché la macchina di sviluppo non lo farebbe.
 *
 * Cambiare questo numero invalida ogni hash già memorizzato: le password esistenti sono
 * state derivate con il valore precedente e non si ricalcolano senza conoscerle. Una
 * modifica va accompagnata da una migrazione che rigeneri i hash del seed e da un reset
 * delle password in essere.
 */
export const ITERAZIONI = 100_000;
const LUNGHEZZA_BIT = 256;
const BYTE_SALE = 16;

/** Alfabeto privo di simboli ambigui: niente 0/O, 1/l/I, niente segni di interpunzione. */
const ALFABETO = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';

/** Lunghezza minima imposta al cambio password (schermata 6f). */
export const LUNGHEZZA_MINIMA_PASSWORD = 10;

function inBase64(byte: Uint8Array): string {
  let s = '';
  for (const b of byte) s += String.fromCharCode(b);
  return btoa(s);
}

function daBase64(testo: string): Uint8Array {
  const bin = atob(testo);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

async function deriva(password: string, sale: Uint8Array): Promise<string> {
  const chiave = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(password),
    'PBKDF2',
    false,
    ['deriveBits'],
  );
  const bit = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', salt: sale as BufferSource, iterations: ITERAZIONI, hash: 'SHA-256' },
    chiave,
    LUNGHEZZA_BIT,
  );
  return inBase64(new Uint8Array(bit));
}

/** Genera sale e hash per una password nuova. */
export async function hashPassword(
  password: string,
): Promise<{ hash: string; sale: string }> {
  const sale = crypto.getRandomValues(new Uint8Array(BYTE_SALE));
  return { hash: await deriva(password, sale), sale: inBase64(sale) };
}

/** Confronto a tempo costante fra l'hash calcolato e quello memorizzato. */
export async function verificaPassword(
  password: string,
  hashAtteso: string,
  sale: string,
): Promise<boolean> {
  const calcolato = await deriva(password, daBase64(sale));
  const a = new TextEncoder().encode(calcolato);
  const b = new TextEncoder().encode(hashAtteso);
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a[i] ^ b[i];
  return diff === 0;
}

/** Password casuale di 12 caratteri, alfabeto privo di simboli ambigui (§ 7). */
export function generaPassword(lunghezza = 12): string {
  const n = new Uint32Array(lunghezza);
  crypto.getRandomValues(n);
  let out = '';
  for (let i = 0; i < lunghezza; i++) out += ALFABETO[n[i] % ALFABETO.length];
  return out;
}
